import type {
  RouteLocationNamedRaw,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  Router,
} from 'vue-router';
import {
  toRef,
  watch,
} from 'vue';

import type { IAppConfigMerchant } from '@modules/merchant/types';
import { ERouteNamespace } from '@modules/core/services/router/enums';
import { useMerchantStore } from '@modules/merchant/store';
import {
  watchDeep,
} from '@vueuse/core';

function getMerchantLocation(
  to: (RouteLocationNormalized | RouteLocationNormalizedLoaded),
  merchant: IAppConfigMerchant | undefined,
  keepNotEmpty = false,
): RouteLocationNamedRaw | undefined {
  if (!merchant?.id || (
    keepNotEmpty && to.query.merchant
  )) {
    return undefined;
  }

  if (
    to.meta.namespace === ERouteNamespace.ADMIN
    || to.meta.namespace === ERouteNamespace.AFFILIATE
  ) {
    const merchantQuery = String(merchant?.id) || merchant?.merchant_name || '';

    if (merchantQuery !== to.query.merchant) {
      return {
        name: to.name || undefined,
        params: to.params,
        query: {
          ...to.query,
          merchant: merchantQuery,
        },
      };
    }
  }

  return undefined;
}

function checkMerchantQuery(
  to: RouteLocationNormalized,
): void {
  const merchantStore = useMerchantStore();
  const merchantList = toRef(merchantStore, 'merchantList');

  function checkMerchantList(): boolean {
    if (!merchantList.value.length) {
      return false;
    }

    merchantStore.checkQueryMerchant(to.query.merchant);
    return true;
  }

  if (!checkMerchantList()) {
    const unwatchMerchantList = watchDeep(merchantList, (list) => {
      if (!list.length)
        return;
      merchantStore.checkQueryMerchant(to.query.merchant);
      unwatchMerchantList();
    });
  }
}

export default function createMerchantGuard(router: Router): void {
  const merchantStore = useMerchantStore();
  const merchant = toRef(merchantStore, 'merchant');

  router.beforeEach((to, from, next) => {
    // Set merchant id on first request (router is not ready yet)
    if (!from.name) {
      checkMerchantQuery(to);
    }

    const location = getMerchantLocation(to, merchant.value, true);

    if (location) {
      next(location);
      return;
    }

    next();
  });

  router.afterEach((to) => {
    const location = getMerchantLocation(to, merchant.value);

    if (location) {
      void router.replace(location);
    }
  });

  void router.isReady().then(() => {
    watch(merchant, (newValue) => {
      if (newValue) {
        const location = getMerchantLocation(router.currentRoute.value, newValue);
        if (location) {
          void router.replace(location);
        }
      }
    }, {
      deep: true,
    });
  });
}
